/**
 * Scroll-in animations for sections, blocks and grid items.
 *
 * Config:  snippets/scroll-reveal-config.liquid  (theme.scrollReveal + CSS vars)
 * Styles:  assets/scroll-reveal.css
 * Settings: Theme settings > Scroll animations
 *
 * How targets are chosen — automatically, for every page, without touching
 * individual section files:
 *
 *   - Every `.shopify-section` directly inside #MainContent is a candidate.
 *     Header, footer and popup groups are never touched.
 *   - The first section on the page is the "hero": it only fades (or whatever
 *     the hero setting says).
 *   - A section with no repeated items animates as one block.
 *   - A section that contains repeated items (product cards, collection tiles,
 *     article cards, testimonial slides, promo tiles, …) is split: the items
 *     animate individually and are staggered left-to-right within the row that
 *     enters the viewport together; everything else in the section (heading,
 *     filter bar, buttons) animates as its own block.
 *   - Items added later (AJAX collection filtering, product recommendations,
 *     recently viewed, infinite scroll, theme editor) are picked up by a
 *     MutationObserver and animated the same way.
 *
 * Markup hooks (optional, for future sections):
 *   data-scroll-reveal="off"      on a section's root element: leave it alone
 *   data-scroll-reveal="hero"     on a section's root element: treat as hero
 *   data-scroll-reveal="fade-up"  (or any style name): force that style
 *   data-scroll-reveal-item       on any element: treat it as a staggered item
 *
 * Public API: theme.scrollReveal.refresh(node), theme.scrollReveal.revealAll()
 */
(function () {
  'use strict';

  var cfg = window.theme && window.theme.scrollReveal;
  var html = document.documentElement;
  if (!cfg || !cfg.enabled || cfg._booted) return;
  cfg._booted = true;

  var body = document.body;
  var reducedMotion = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mobileOff = !cfg.mobile && window.matchMedia && matchMedia('(max-width: ' + cfg.mobileBreakpoint + 'px)').matches;
  var supported = 'IntersectionObserver' in window && 'MutationObserver' in window;
  var disabledByTheme = body && body.getAttribute('data-disable-animations') === 'true';

  function bail() {
    html.classList.remove('scroll-reveal');
    html.classList.add('scroll-reveal-off');
  }

  if (reducedMotion || mobileOff || !supported || disabledByTheme) {
    bail();
    return;
  }

  var STYLES = ['fade', 'fade-up', 'fade-down', 'fade-left', 'fade-right', 'zoom-in', 'zoom-out', 'blur-in', 'slide-up'];
  var IGNORED_TAGS = { STYLE: 1, SCRIPT: 1, TEMPLATE: 1, NOSCRIPT: 1, LINK: 1, META: 1 };

  // Repeated items that should stagger. `[data-aos^="row-of-"]` is the theme's
  // own marker for grid cells and covers product cards, collection tiles,
  // article cards, testimonial slides, text columns, footer promos and more.
  var ITEM_SELECTOR = [
    '[data-aos^="row-of-"]',
    '.grid-product',
    '.collection-item',
    '.flex-grid__item',
    '.logo-bar__item',
    '.image-grid__item',
    '.gallery-grid__item',
    '.text-with-icons__block',
    '.support-grid__card',
    '.look-card-carousel__cell',
    '.lookbook-carousel__cell',
    '.image-accordion__item',
    '[data-scroll-reveal-item]'
  ].join(',');

  // Never animated as a block or item: screen-reader text, viewport-pinned
  // bars (the PDP sticky nav is moved into the fixed header wrapper after load),
  // and anything a section opts out with data-scroll-reveal="off".
  var EXCLUDE_SELECTOR = '.visually-hidden, .pdp-sticky-nav, [data-scroll-reveal="off"]';

  var main = document.getElementById('MainContent');
  if (!main) {
    bail();
    return;
  }

  var sectionStyle = normaliseStyle(cfg.style, 'fade-up');
  var itemStyle = normaliseStyle(cfg.itemStyle, sectionStyle);
  var heroStyle = cfg.heroStyle === 'same' ? sectionStyle : (cfg.heroStyle === 'none' ? 'none' : 'fade');
  var baseDelay = toInt(cfg.delay, 0);
  var stagger = toInt(cfg.stagger, 90);
  var staggerMax = Math.max(1, toInt(cfg.staggerMax, 6));
  var once = cfg.once !== false;
  var useItems = cfg.items !== false;
  var trigger = Math.min(40, Math.max(0, toInt(cfg.trigger, 8)));
  var rtl = (html.getAttribute('dir') || '').toLowerCase() === 'rtl';

  var timers = new WeakMap();

  var observer = new IntersectionObserver(onIntersect, {
    root: null,
    rootMargin: '0px 0px -' + trigger + '% 0px',
    threshold: 0
  });

  /* ------------------------------------------------------------------ utils */

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function normaliseStyle(value, fallback) {
    return STYLES.indexOf(value) !== -1 ? value : fallback;
  }

  function isVisualElement(el) {
    if (!el || el.nodeType !== 1 || IGNORED_TAGS[el.tagName]) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 || rect.height > 0;
  }

  function isAnimatableBlock(el) {
    // Blocks and items must have a real box: skips screen-reader-only text,
    // spacers and empty wrappers that would otherwise be "animated" invisibly.
    if (!isVisualElement(el) || el.matches(EXCLUDE_SELECTOR)) return false;
    var rect = el.getBoundingClientRect();
    if (rect.width <= 1 || rect.height <= 1) return false;
    // Fixed / sticky elements (sticky PDP nav, sticky add-to-cart bars) are
    // positioned against the viewport; sliding them around looks broken.
    var position = getComputedStyle(el).position;
    return position !== 'fixed' && position !== 'sticky';
  }

  function isItem(el) {
    return el.nodeType === 1 && el.matches(ITEM_SELECTOR);
  }

  function isTopLevelItem(el) {
    var parent = el.parentElement;
    return !(parent && parent.closest(ITEM_SELECTOR));
  }

  function isProcessed(el) {
    return el.classList.contains('sr-el');
  }

  function sectionOf(el) {
    return el.closest('.shopify-section');
  }

  function sectionRoot(section) {
    // The section's own root element (skipping <style>/<script> emitted before it).
    var child = section.firstElementChild;
    while (child && IGNORED_TAGS[child.tagName]) child = child.nextElementSibling;
    return child;
  }

  function sectionHint(section) {
    var root = sectionRoot(section);
    var hint = root && root.getAttribute('data-scroll-reveal');
    return hint ? hint.trim().toLowerCase() : '';
  }

  function durationFor(el) {
    if (el.classList.contains('sr-hero')) return toInt(cfg.heroDuration, 900);
    if (el.classList.contains('sr-item')) return toInt(cfg.itemDuration, 600);
    return toInt(cfg.duration, 700);
  }

  /* ------------------------------------------------------------ state moves */

  function prepare(el, role, style) {
    el.classList.add('sr-el', 'sr-' + role, 'sr-style-' + style, 'sr-hidden');
    observer.observe(el);
  }

  function reveal(el, delay) {
    if (!el.classList.contains('sr-hidden')) return;
    var total = Math.max(0, delay || 0);
    el.style.setProperty('--sr-delay', total + 'ms');
    el.classList.add('sr-anim');
    el.classList.remove('sr-hidden');

    clearTimeout(timers.get(el));
    timers.set(el, setTimeout(function () {
      finish(el);
    }, total + durationFor(el) + 80));
  }

  function revealInstantly(el) {
    clearTimeout(timers.get(el));
    el.classList.remove('sr-hidden', 'sr-anim');
    finish(el);
  }

  function finish(el) {
    el.classList.remove('sr-anim');
    el.style.removeProperty('--sr-delay');
    if (el.getAttribute('style') === '') el.removeAttribute('style');
    el.classList.add('sr-done');
    if (once) {
      observer.unobserve(el);
    }
  }

  function rehide(el) {
    // Repeat mode only: element scrolled completely out of the real viewport.
    clearTimeout(timers.get(el));
    el.classList.remove('sr-done', 'sr-anim');
    el.style.removeProperty('--sr-delay');
    el.classList.add('sr-hidden');
  }

  /* ------------------------------------------------------- IO callback */

  function onIntersect(entries) {
    var entering = [];

    entries.forEach(function (entry) {
      var el = entry.target;

      if (entry.isIntersecting) {
        if (el.classList.contains('sr-hidden')) entering.push(entry);
        return;
      }

      var rect = entry.boundingClientRect;
      if (rect.bottom <= 0 && el.classList.contains('sr-hidden')) {
        // Already above the viewport without ever having been seen: the page
        // opened scrolled down (anchor link, restored position) or the visitor
        // jumped past it. Show it as-is so scrolling back up is calm instead of
        // having content slide up while they scroll up.
        revealInstantly(el);
        return;
      }

      if (!once && el.classList.contains('sr-done')) {
        var vh = window.innerHeight || html.clientHeight;
        if (rect.bottom <= 0 || rect.top >= vh) rehide(el);
      }
    });

    if (entering.length) scheduleBatch(entering);
  }

  function scheduleBatch(entries) {
    // Everything that entered together is staggered in reading order, grouped
    // per section so two sections never share one cascade. Cards in a row go
    // left to right; a product page's blocks cascade top to bottom.
    var groups = new Map();

    entries.forEach(function (entry) {
      var key = sectionOf(entry.target) || main;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(entry);
    });

    groups.forEach(function (list) {
      readingOrder(list).forEach(function (entry, index) {
        var step = Math.min(index, staggerMax - 1);
        reveal(entry.target, baseDelay + step * stagger);
      });
    });
  }

  function readingOrder(entries) {
    // Cluster into rows first (tops within half an item height count as the
    // same row, so slightly offset editorial layouts still read left to right),
    // then order each row horizontally.
    var minHeight = Infinity;
    entries.forEach(function (entry) {
      minHeight = Math.min(minHeight, entry.boundingClientRect.height || Infinity);
    });
    var tolerance = Math.max(24, isFinite(minHeight) ? minHeight / 2 : 24);

    entries.sort(function (a, b) {
      return a.boundingClientRect.top - b.boundingClientRect.top;
    });

    var rows = [];
    var current = null;
    entries.forEach(function (entry) {
      var top = entry.boundingClientRect.top;
      if (!current || top - current.top > tolerance) {
        current = { top: top, list: [] };
        rows.push(current);
      }
      current.list.push(entry);
    });

    var ordered = [];
    rows.forEach(function (row) {
      row.list.sort(function (a, b) {
        var la = a.boundingClientRect.left;
        var lb = b.boundingClientRect.left;
        return rtl ? lb - la : la - lb;
      });
      ordered = ordered.concat(row.list);
    });
    return ordered;
  }

  /* ---------------------------------------------------- target discovery */

  function collectItems(scope) {
    if (!useItems) return [];
    var found = Array.prototype.slice.call(scope.querySelectorAll(ITEM_SELECTOR));
    if (isItem(scope)) found.unshift(scope);
    return found.filter(function (el) {
      return !isProcessed(el) && isTopLevelItem(el) && isAnimatableBlock(el);
    });
  }

  function collectBlocks(node, out) {
    // Walk down. A child that holds no items is an animated block of its own;
    // a child that holds items is descended into; items themselves are skipped.
    Array.prototype.forEach.call(node.children, function (child) {
      if (IGNORED_TAGS[child.tagName] || isItem(child) || isProcessed(child)) return;
      if (child.querySelector(ITEM_SELECTOR)) {
        collectBlocks(child, out);
      } else if (isAnimatableBlock(child)) {
        out.push(child);
      }
    });
    return out;
  }

  function processSection(section, isFirst) {
    if (isProcessed(section) || section.hasAttribute('data-sr-processed')) return;
    section.setAttribute('data-sr-processed', '');

    if (!isVisualElement(section)) return; // empty / hidden section: nothing to do

    var hint = sectionHint(section);
    if (hint === 'off') return;

    var items = collectItems(section);
    var asHero = hint === 'hero' || (isFirst && hint === '' && !items.length);
    var forcedStyle = STYLES.indexOf(hint) !== -1 ? hint : null;

    if (asHero) {
      if (heroStyle === 'none') return;
      prepare(section, 'hero', forcedStyle || heroStyle);
      return;
    }

    if (!items.length) {
      prepare(section, 'section', forcedStyle || sectionStyle);
      return;
    }

    collectBlocks(section, []).forEach(function (block) {
      prepare(block, 'block', forcedStyle || sectionStyle);
    });
    items.forEach(function (item) {
      prepare(item, 'item', forcedStyle || itemStyle);
    });
  }

  function firstSection() {
    var child = main.firstElementChild;
    while (child) {
      if (child.classList.contains('shopify-section') && isVisualElement(child)) return child;
      child = child.nextElementSibling;
    }
    return null;
  }

  function processAddedNode(node) {
    if (node.nodeType !== 1 || !main.contains(node)) return;

    if (node.classList.contains('shopify-section') && node.parentElement === main) {
      processSection(node, node === firstSection());
      return;
    }

    var sections = node.querySelectorAll ? node.querySelectorAll('.shopify-section') : [];
    Array.prototype.forEach.call(sections, function (section) {
      if (section.parentElement === main) processSection(section, section === firstSection());
    });

    if (!useItems) return;
    var section = sectionOf(node);
    if (section && section.hasAttribute('data-sr-processed') && sectionHint(section) !== 'off') {
      var forcedStyle = STYLES.indexOf(sectionHint(section)) !== -1 ? sectionHint(section) : null;
      collectItems(node).forEach(function (item) {
        prepare(item, 'item', forcedStyle || itemStyle);
      });
    }
  }

  /* ------------------------------------------------------------ safety */

  function revealVisibleAtBottom() {
    // Short last sections can sit entirely inside the trigger margin at the end
    // of the page and would otherwise never intersect. Once the page is scrolled
    // to the bottom, show anything that is inside the real viewport.
    var doc = html;
    var atBottom = (window.innerHeight + (window.pageYOffset || doc.scrollTop)) >= (doc.scrollHeight - 2);
    if (!atBottom) return;
    var vh = window.innerHeight;
    var pending = main.querySelectorAll('.sr-el.sr-hidden');
    var entries = [];
    Array.prototype.forEach.call(pending, function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < vh && rect.bottom > 0) entries.push({ target: el, boundingClientRect: rect });
    });
    if (entries.length) scheduleBatch(entries);
  }

  function revealSkippedAbove() {
    // A long jump (anchor link, scrollbar drag, "back to top") can move hidden
    // elements from below the viewport to above it without them ever
    // intersecting, so IntersectionObserver never reports them. Show those
    // as-is rather than have them slide up later while the visitor scrolls up.
    var pending = main.querySelectorAll('.sr-el.sr-hidden');
    Array.prototype.forEach.call(pending, function (el) {
      if (el.getBoundingClientRect().bottom <= 0) revealInstantly(el);
    });
  }

  var scrollTicking = false;
  var lastScrollY = window.pageYOffset || 0;
  window.addEventListener('scroll', function () {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(function () {
      scrollTicking = false;
      var y = window.pageYOffset || 0;
      var jumped = Math.abs(y - lastScrollY) > (window.innerHeight || 0) * 0.8;
      lastScrollY = y;
      if (jumped) revealSkippedAbove();
      revealVisibleAtBottom();
    });
  }, { passive: true });

  function revealAll(scope) {
    var pending = (scope || main).querySelectorAll('.sr-el.sr-hidden');
    Array.prototype.forEach.call(pending, function (el) {
      reveal(el, 0);
    });
    if (scope && scope.classList && scope.classList.contains('sr-hidden')) reveal(scope, 0);
  }

  function revealInView(scope) {
    // Used by the theme editor. Everything hidden inside `scope` that is on
    // screen right now animates in with the normal stagger; anything already
    // above the viewport shows as-is; anything below waits for the observer.
    scope = scope || main;
    var vh = window.innerHeight || html.clientHeight;
    var pending = Array.prototype.slice.call(scope.querySelectorAll('.sr-el.sr-hidden'));
    if (scope.classList && scope.classList.contains('sr-hidden')) pending.unshift(scope);
    var entries = [];
    pending.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.bottom <= 0) {
        revealInstantly(el);
      } else if (rect.top < vh) {
        entries.push({ target: el, boundingClientRect: rect });
      }
    });
    if (entries.length) scheduleBatch(entries);
  }

  /* -------------------------------------------------------------- boot */

  function init() {
    var first = firstSection();
    Array.prototype.forEach.call(main.children, function (child) {
      if (child.classList.contains('shopify-section')) processSection(child, child === first);
    });
    if (useItems) html.classList.add('scroll-reveal-items');
    html.classList.add('scroll-reveal-ready');
  }

  init();

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      Array.prototype.forEach.call(mutation.addedNodes, processAddedNode);
    });
  }).observe(main, { childList: true, subtree: true });

  // Theme editor: newly rendered sections animate in. Selecting a section or
  // block (which the editor also does on every reload for the section being
  // edited) plays the normal staggered entrance for what is on screen, so the
  // merchant sees the same thing a visitor would, never a gap and never
  // everything popping in at once.
  if (cfg.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      processAddedNode(event.target);
    });
    document.addEventListener('shopify:section:select', function (event) {
      revealInView(event.target);
    });
    document.addEventListener('shopify:block:select', function (event) {
      var section = sectionOf(event.target);
      if (section) revealInView(section);
    });
  }

  // Leaving via bfcache and returning: show everything, no replay.
  window.addEventListener('pagehide', function () {
    var pending = main.querySelectorAll('.sr-el.sr-hidden');
    Array.prototype.forEach.call(pending, revealInstantly);
  });

  cfg.refresh = function (node) {
    processAddedNode(node || main);
  };
  cfg.revealAll = function () {
    revealAll();
  };
  cfg.revealInView = revealInView;
})();
